import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Divider,
    TextField,
    MenuItem
} from "@mui/material";
import Swal from "sweetalert2";
import moment from "moment";
import { auth, db } from "../../firebase";
import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    Timestamp,
    where
} from "firebase/firestore";
import Menu from "../../panel/Menu";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(15px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .page-fade-in {
    animation: fadeIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  }

  /* --- METICULOUS PRINT STYLES --- */
  @media print {
    @page {
        size: A4;
        margin: 5mm;
    }

    html, body, #root {
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        color: #000 !important;
    }

    .no-print, nav, header, footer, .sidebar, button, .MuiTextField-root {
        display: none !important;
    }

    * {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
        text-shadow: none !important;
        animation: none !important;
        border-color: #ccc !important;
    }

    .page-fade-in {
        padding: 0 !important;
        margin: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
    }

    .print-area {
        border: none !important;
        background: #fff !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
    }

    table {
        width: 100% !important;
        border-collapse: collapse !important;
    }
    
    th, td {
        border: 1px solid #ddd !important;
        padding: 6px 8px !important;
        color: #000 !important;
    }

    th {
        background-color: #f5f5f5 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        font-weight: bold !important;
    }
  }
`;

function Bookingtypewise() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    background: colors.calPolyGreen,
    color: colors.vanilla
  });

  const printRef = useRef();

  const [propertyId, setPropertyId] = useState("");
  
  // Default values set to today's date
  const [fromDate, setFromDate] = useState(moment().format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  
  const [bookType, setBookType] = useState("");
  const [bookingTypes, setBookingTypes] = useState([]);
  const [bookingData, setBookingData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  /* 🔐 AUTH + PROPERTY */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      const roleSnap = await getDoc(doc(db, "authroles", user.uid));
      if (roleSnap.exists()) {
        const pid = roleSnap.data().propertyId;
        setPropertyId(pid);
        fetchBookingTypes(pid);
      } else {
        Toast.fire({ icon: "error", title: "Property not assigned" });
      }
    });

    return () => unsub();
  }, []);

  // Auto-trigger report once booking types are loaded and a default booking type is selected
  useEffect(() => {
    if (propertyId && bookType && fromDate && toDate) {
      getReport();
    }
  }, [propertyId, bookType]);

  /* 📌 FETCH BOOKING TYPES */
  const fetchBookingTypes = async (pid) => {
    const snap = await getDocs(
      query(collection(db, "bookingtypes"), where("propertyId", "==", pid))
    );

    const types = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setBookingTypes(types);

    // Automatically select the first booking type if available
    if (types.length > 0 && !bookType) {
      setBookType(types[0].bookingtype);
    }
  };

  /* 📊 GET REPORT */
  const getReport = async () => {
    if (!fromDate || !toDate || !bookType) {
      Toast.fire({ icon: "error", title: "Select Booking Type & Dates" });
      return;
    }

    setLoading(true);
    setBookingData([]);
    setTotal(0);

    const start = new Date(fromDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(toDate);
    end.setDate(end.getDate() + 1);

    try {
      const q = query(
        collection(db, "reservations"),
        where("propertyId", "==", propertyId),
        where("bookingsource", "==", bookType),
        where("createdAt", ">=", Timestamp.fromDate(start)),
        where("createdAt", "<", Timestamp.fromDate(end))
      );

      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const sum = data.reduce((s, b) => s + (b.totalamount || 0), 0);

      setBookingData(data);
      setTotal(sum.toFixed(2));
    } catch (err) {
      console.error(err);
      Toast.fire({ icon: "error", title: "Error loading report" });
    } finally {
      setLoading(false);
    }
  };

  /* 🖨 PRINT */
  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }} className="page-fade-in">
      <style>{animationStyles}</style>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>

        <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }} className="no-print">
          Booking Type Wise Report
        </Typography>

        {/* FILTER CARD */}
        <Card sx={{ mb: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }} className="no-print">
          <CardContent sx={{ p: 3 }}>
            <div className="container-fluid p-0">
              <div className="row align-items-center">
                <div className="col-sm-3 mb-3 mb-sm-0">
                  <TextField
                    select
                    label="Booking Type"
                    value={bookType}
                    onChange={(e) => setBookType(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: colors.vanilla } }}
                    sx={{ 
                      color: colors.vanilla,
                      backgroundColor: colors.darkGreen,
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": { 
                        borderRadius: "8px",
                        "& fieldset": { borderColor: colors.goldenrod },
                        "&:hover fieldset": { borderColor: colors.gold },
                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                      },
                      '& .MuiSelect-select': { color: colors.vanilla },
                      '& .MuiSvgIcon-root': { color: colors.vanilla }
                    }}
                  >
                    <MenuItem value="" sx={{ bgcolor: colors.darkGreen, color: colors.vanilla }}>Select</MenuItem>
                    {bookingTypes.map(bt => (
                      <MenuItem key={bt.id} value={bt.bookingtype} sx={{ bgcolor: colors.darkGreen, color: colors.vanilla, '&:hover': { bgcolor: colors.calPolyGreen } }}>
                        {bt.bookingtype}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>

                <div className="col-sm-3 mb-3 mb-sm-0">
                  <TextField
                    type="date"
                    label="From Date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                    InputProps={{ style: { color: colors.vanilla } }}
                    sx={{ 
                      "& .MuiOutlinedInput-root": { 
                        borderRadius: "8px",
                        backgroundColor: colors.darkGreen,
                        "& fieldset": { borderColor: colors.goldenrod },
                        "&:hover fieldset": { borderColor: colors.gold },
                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                      } 
                    }}
                  />
                </div>

                <div className="col-sm-3 mb-3 mb-sm-0">
                  <TextField
                    type="date"
                    label="To Date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true, style: { color: colors.vanilla } }}
                    InputProps={{ style: { color: colors.vanilla } }}
                    sx={{ 
                      "& .MuiOutlinedInput-root": { 
                        borderRadius: "8px",
                        backgroundColor: colors.darkGreen,
                        "& fieldset": { borderColor: colors.goldenrod },
                        "&:hover fieldset": { borderColor: colors.gold },
                        "&.Mui-focused fieldset": { borderColor: colors.gold }
                      } 
                    }}
                  />
                </div>

                <div className="col-sm-3">
                  <Button 
                    variant="contained" 
                    fullWidth 
                    disabled={loading}
                    onClick={getReport}
                    sx={{ 
                      bgcolor: colors.goldenrod, 
                      color: colors.darkGreen, 
                      fontWeight: 700, 
                      textTransform: "none", 
                      borderRadius: "8px",
                      height: "40px",
                      '&:hover': { bgcolor: colors.gold } 
                    }}
                  >
                    {loading ? "Loading..." : "Get Report"}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* REPORT */}
        <Card className="print-area" ref={printRef} sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <div className="container-fluid p-0 mb-3">
              <div className="row align-items-center text-center">
                <div className="col-sm-4 mb-2 mb-sm-0">
                  <Typography sx={{ color: colors.vanilla }}>
                    From : {fromDate ? moment(fromDate).format("DD/MM/YYYY") : "—"}
                  </Typography>
                </div>
                <div className="col-sm-4 mb-2 mb-sm-0">
                  <Typography fontWeight={700} sx={{ color: colors.gold }}>
                    {bookType || "BOOKING TYPE"} REPORT
                  </Typography>
                </div>
                <div className="col-sm-4">
                  <Typography sx={{ color: colors.vanilla }}>
                    To : {toDate ? moment(toDate).format("DD/MM/YYYY") : "—"}
                  </Typography>
                </div>
              </div>
            </div>

            <Divider sx={{ mb: 3, borderColor: colors.goldenrod, opacity: 0.4 }} />

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent", mb: 3 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: colors.darkGreen }}>
                  <TableRow>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>#</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Name</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Mobile</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Check-In</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Check-Out</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Rooms</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Adults</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Type</TableCell>
                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Paid</TableCell>
                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Remaining</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {bookingData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                  {bookingData.map((b, i) => (
                    <TableRow key={b.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{i + 1}</TableCell>
                      <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.customers?.custname}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.customers?.mobile}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{moment(b.checkindate).format("DD/MM/YYYY")}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, opacity: 0.9, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{moment(b.checkoutdate).format("DD/MM/YYYY")}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        {b.rooms?.map((r, j) => (
                          <span key={j}>{r.roomno}, </span>
                        ))}
                      </TableCell>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.adults}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.bookingsource}</TableCell>
                      <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.totalamount}</TableCell>
                      <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.advance}</TableCell>
                      <TableCell align="right" sx={{ color: colors.gold, fontWeight: 700, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{b.remaining}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Paper elevation={0} sx={{ mt: 3, p: 2, textAlign: "center", bgcolor: colors.darkGreen, border: `1px solid ${colors.goldenrod}`, borderRadius: 2 }}>
              <Typography fontWeight={700} sx={{ color: colors.gold }}>
                Total Booking Amount : ₹ {total}
              </Typography>
            </Paper>
          </CardContent>
        </Card>

        <Box textAlign="center" mt={3} className="no-print">
          <Button 
            variant="contained" 
            onClick={handlePrint}
            sx={{ 
              bgcolor: colors.goldenrod, 
              color: colors.darkGreen, 
              fontWeight: 700, 
              textTransform: "none", 
              borderRadius: "8px",
              px: 4,
              py: 1.2,
              '&:hover': { bgcolor: colors.gold } 
            }}
          >
            Print Report
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default Bookingtypewise;